/**
 * The prompt library (batch 1 of the Manifest AI gap work): ready-made things
 * to ask Master Kala, grouped by category. Tapping one fills the composer and
 * sends nothing, so a member edits the square-bracket placeholders first.
 *
 * Like breathwork, the set ships in code so a fix reaches every tenant: an
 * admin row of kind `coach_prompt` with the same key replaces a prompt (or
 * hides it), and a keyless row adds one of the tenant's own. Everything here
 * is written in the member's voice, because it is the member who sends it.
 *
 * The Scripts category is the one written for this audience: members recruit
 * and sell, so an invite, a follow-up and a product post are worth more to
 * them than to a general manifestation crowd. None of it may promise money or
 * health outcomes; `coach-prompts.test.ts` greps for the words, and the
 * scripts rule in `prompt-coaching.ts` holds Kala's replies to the same line.
 */

export const COACH_PROMPT_CATEGORIES = [
  'money',
  'business',
  'focus',
  'relationships',
  'mindset',
  'scripts',
] as const;
export type CoachPromptCategory = (typeof COACH_PROMPT_CATEGORIES)[number];

export const COACH_PROMPT_CATEGORY_LABELS: Record<CoachPromptCategory, string> = {
  money: 'Money',
  business: 'Business',
  focus: 'Focus',
  relationships: 'Relationships',
  mindset: 'Mindset',
  scripts: 'Scripts',
};

/** One line under each category heading. */
export const COACH_PROMPT_CATEGORY_HINTS: Record<CoachPromptCategory, string> = {
  money: 'Habits, honest looks and decisions, without advice on what to buy.',
  business: 'Planning the week, following up, and the first hour of the day.',
  focus: 'One thing, fewer distractions, and a morning you can keep.',
  relationships: 'Appreciation, hard conversations and boundaries.',
  mindset: 'Beliefs, fears, confidence and the picture of the finish.',
  scripts: 'Messages in your own voice. Fill in the [brackets] before you send.',
};

export const BUILT_IN_COACH_PROMPT_KEYS = [
  'money-one-habit',
  'money-honest-look',
  'money-belief',
  'money-goal-step',
  'money-gratitude',
  'money-decision',
  'business-week-plan',
  'business-follow-ups',
  'business-stuck',
  'business-daily-rhythm',
  'business-review',
  'business-first-hour',
  'focus-one-thing',
  'focus-distraction',
  'focus-morning',
  'focus-putting-off',
  'focus-energy',
  'focus-reset',
  'relationships-appreciation',
  'relationships-hard-conversation',
  'relationships-boundary',
  'relationships-guidance',
  'relationships-team',
  'relationships-listening',
  'mindset-belief',
  'mindset-fear',
  'mindset-confidence',
  'mindset-wins',
  'mindset-affirmations',
  'mindset-picture',
  'scripts-invite',
  'scripts-follow-up',
  'scripts-product-post',
  'scripts-pyramid-question',
  'scripts-no-time',
  'scripts-my-story',
] as const;
export type BuiltInCoachPromptKey = (typeof BUILT_IN_COACH_PROMPT_KEYS)[number];

export type BuiltInCoachPrompt = {
  key: BuiltInCoachPromptKey;
  category: CoachPromptCategory;
  /** The short label on the row. */
  title: string;
  /** What lands in the composer, in the member's voice. */
  body: string;
};

/** Bounds an admin-written prompt must stay inside (the zod schema repeats them). */
export const COACH_PROMPT_LIMITS = { titleMax: 80, bodyMax: 600 } as const;

export const BUILT_IN_COACH_PROMPTS: readonly BuiltInCoachPrompt[] = [
  // ── money ────────────────────────────────────────────────────────────────
  {
    key: 'money-one-habit',
    category: 'money',
    title: 'One money habit',
    body: 'Help me choose one money habit I can keep for the next 30 days. Ask me what I already do well, then propose it as an action for tomorrow.',
  },
  {
    key: 'money-honest-look',
    category: 'money',
    title: 'An honest look at my spending',
    body: 'I want to look honestly at where my money goes. Give me three reflective questions, one at a time, and help me notice a pattern without judging it.',
  },
  {
    key: 'money-belief',
    category: 'money',
    title: 'What I believe about money',
    body: 'Help me put into words what I believe about money and where that belief came from. Then help me write one truer sentence I can practise saying.',
  },
  {
    key: 'money-goal-step',
    category: 'money',
    title: 'Next step on my money goal',
    body: 'Look at my money goal and tell me the smallest step that moves it this week. Propose it as an action.',
  },
  {
    key: 'money-gratitude',
    category: 'money',
    title: 'Money gratitude',
    body: 'Guide me through a short money gratitude practice: three things money already makes possible in my life today.',
  },
  {
    key: 'money-decision',
    category: 'money',
    title: 'A money decision',
    body: 'I have a money decision to make. Ask me what it is, then help me think it through with the questions a calm mentor would ask. Do not tell me what to choose.',
  },
  // ── business ─────────────────────────────────────────────────────────────
  {
    key: 'business-week-plan',
    category: 'business',
    title: 'Plan my business week',
    body: 'Help me plan this week for my business. Ask me my top priority, then propose up to three actions, with the most important one first.',
  },
  {
    key: 'business-follow-ups',
    category: 'business',
    title: 'Who to follow up with',
    body: 'Help me decide which three people I should follow up with this week and why. Then propose the first follow-up as an action for tomorrow.',
  },
  {
    key: 'business-stuck',
    category: 'business',
    title: 'Stuck on a decision',
    body: 'I feel stuck on a business decision. Ask me what the two options are, then help me weigh them against my primary goal.',
  },
  {
    key: 'business-daily-rhythm',
    category: 'business',
    title: 'A daily business rhythm',
    body: 'Help me design a simple daily rhythm for my business: when I reach out to people, when I follow up, when I learn. Keep it to what fits my real day.',
  },
  {
    key: 'business-review',
    category: 'business',
    title: 'Review my business week',
    body: 'Help me review my business week: what worked, what did not, and the one thing to change next week.',
  },
  {
    key: 'business-first-hour',
    category: 'business',
    title: 'My best first hour',
    body: 'What is the best way to spend the first hour of my working day, given my goal? Propose it as tomorrow’s #1 action.',
  },
  // ── focus ────────────────────────────────────────────────────────────────
  {
    key: 'focus-one-thing',
    category: 'focus',
    title: 'One thing today',
    body: 'I have too much on my plate. Help me choose the one thing that matters most today and let the rest wait.',
  },
  {
    key: 'focus-distraction',
    category: 'focus',
    title: 'My biggest distraction',
    body: 'Help me name my biggest distraction this week and design one small guard against it that I can start tomorrow.',
  },
  {
    key: 'focus-morning',
    category: 'focus',
    title: 'A morning I can keep',
    body: 'Help me shape a morning I can actually keep: the first three things I do after waking. Keep it under twenty minutes.',
  },
  {
    key: 'focus-putting-off',
    category: 'focus',
    title: 'Something I keep putting off',
    body: 'There is a task I keep putting off. Ask me what it is, help me find the smallest first move, and propose it as an action.',
  },
  {
    key: 'focus-energy',
    category: 'focus',
    title: 'Match tasks to my energy',
    body: 'Help me match my hardest task to my best hours. Ask me when my energy is highest, then plan the day around it.',
  },
  {
    key: 'focus-reset',
    category: 'focus',
    title: 'Reset after a rough day',
    body: 'Today did not go to plan. Help me close it kindly and set one clear intention for tomorrow.',
  },
  // ── relationships ────────────────────────────────────────────────────────
  {
    key: 'relationships-appreciation',
    category: 'relationships',
    title: 'Say thank you',
    body: 'Help me write a short, honest message of appreciation to someone who has supported me. No selling, just thanks.',
  },
  {
    key: 'relationships-hard-conversation',
    category: 'relationships',
    title: 'A hard conversation',
    body: 'I need to have a hard conversation with someone. Help me get clear on what I want to say, and how to say it calmly.',
  },
  {
    key: 'relationships-boundary',
    category: 'relationships',
    title: 'Set a boundary',
    body: 'Help me set a boundary I have been avoiding. Ask me what keeps happening, then help me word it kindly and clearly.',
  },
  {
    key: 'relationships-guidance',
    category: 'relationships',
    title: 'Ask someone for advice',
    body: 'Help me write a message asking someone I respect for twenty minutes of their advice, with a clear reason and an easy way to say no.',
  },
  {
    key: 'relationships-team',
    category: 'relationships',
    title: 'Support my team',
    body: 'Help me think about how to support the people I work with this week: one thing I can do for each person I lead.',
  },
  {
    key: 'relationships-listening',
    category: 'relationships',
    title: 'Listen better',
    body: 'Help me become a better listener in my next conversation. Give me two things to practise and one question to ask.',
  },
  // ── mindset ──────────────────────────────────────────────────────────────
  {
    key: 'mindset-belief',
    category: 'mindset',
    title: 'A belief that holds me back',
    body: 'Help me find one belief that holds me back and write a truer sentence I can practise instead.',
  },
  {
    key: 'mindset-fear',
    category: 'mindset',
    title: 'Name a fear',
    body: 'I am afraid of something on the way to my goal. Help me name it plainly and find the next small step I can take anyway.',
  },
  {
    key: 'mindset-confidence',
    category: 'mindset',
    title: 'Confidence before a call',
    body: 'I have an important call soon. Help me get calm and confident in five minutes: what to remember, what to let go of.',
  },
  {
    key: 'mindset-wins',
    category: 'mindset',
    title: 'Remember my wins',
    body: 'Help me list three things I have done well recently that I tend to forget, and what they say about me.',
  },
  {
    key: 'mindset-affirmations',
    category: 'mindset',
    title: 'Affirmations for my goal',
    body: 'Write three affirmations tied to my goal, in the present tense, that I could say every morning.',
  },
  {
    key: 'mindset-picture',
    category: 'mindset',
    title: 'Picture the finish',
    body: 'Guide me through picturing my goal already done: what I see, what I hear, how I carry myself. Then help me pick one action that fits that picture.',
  },
  // ── scripts ──────────────────────────────────────────────────────────────
  {
    key: 'scripts-invite',
    category: 'scripts',
    title: 'Invite a friend',
    body: 'Write a short invite message to [name] about [what I do]. Honest, warm and no pressure. No claims about money. End with one easy question.',
  },
  {
    key: 'scripts-follow-up',
    category: 'scripts',
    title: 'Follow up after silence',
    body: 'Write a friendly follow-up to [name], who has not replied to my message about [topic]. Light, respectful of their time, and easy to say no to.',
  },
  {
    key: 'scripts-product-post',
    category: 'scripts',
    title: 'A product post',
    body: 'Write a short social post about [product] in my own voice: what it is, who it is for, and how I use it. No claims about health or money, one clear call to action.',
  },
  {
    key: 'scripts-pyramid-question',
    category: 'scripts',
    title: '“Is this a pyramid scheme?”',
    body: 'Someone asked me if what I do is a pyramid scheme. Help me answer calmly and honestly, in two or three sentences, and invite a real conversation.',
  },
  {
    key: 'scripts-no-time',
    category: 'scripts',
    title: 'They have no time',
    body: '[name] said they have no time. Write me a reply that respects that, offers one small next step, and leaves the door open.',
  },
  {
    key: 'scripts-my-story',
    category: 'scripts',
    title: 'My story in sixty seconds',
    body: 'Help me tell my story in sixty seconds: why I started, what changed for me, and what I hope for the person listening. Truthful, no exaggeration.',
  },
];

export function isBuiltInCoachPromptKey(key: string | null): key is BuiltInCoachPromptKey {
  return key !== null && (BUILT_IN_COACH_PROMPT_KEYS as readonly string[]).includes(key);
}

export function isCoachPromptCategory(v: unknown): v is CoachPromptCategory {
  return typeof v === 'string' && (COACH_PROMPT_CATEGORIES as readonly string[]).includes(v);
}
